import { useCallback } from "react";
import { useTixologi } from "../../legacy/hooks/tixologi/useTixologi";

/**
 * Wraps `useTixologi` to expose a `refresh` callback that forces a re-fetch
 * of the venue/comedian Tixologi token and resolves to the new token (or
 * null if the refresh failed).
 *
 * Use this when calling Tixologi mutations directly from the client: pass
 * `refresh` as the `refreshToken` arg to the client function so that a 401
 * caused by an expired token triggers a single transparent retry.
 *
 * `isSettled` is true once the login has answered, with a token or without
 * one, and when there is no `id` to log in with. A settled login with a null
 * `token` will not produce one by itself: offer `refresh` instead of waiting.
 */
export function useTixologiTokenRefresher(
  id: string | undefined,
  isComedian = false
): {
  token: string | null;
  isSettled: boolean;
  refresh: () => Promise<string | null>;
} {
  const { tixologiData, refetch } = useTixologi({ id, isComedian });
  const token = tixologiData?.token ?? null;
  const isSettled = !id || tixologiData !== undefined;

  const refresh = useCallback(async () => {
    // A refresh that overlaps another login shares it: cancelling the first would hand its caller the old data.
    const result = await refetch({ cancelRefetch: false });
    return result.data?.token ?? null;
  }, [refetch]);

  return { token, isSettled, refresh };
}
